import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type {
  SeasonRebidDto,
  SeasonReminderDto,
} from '@lib/dto/season-reminder.dto'
import type { SeasonReminderActionInput } from '@lib/input/season-reminder.input'
import { seasonReminderService } from '@/services/SeasonReminderService'
import { clientKeys, proposalKeys } from '@/hooks/queryKeys'

/** Seasons of accepted work due for a rebid: the "Coming up" list. */
export function useSeasonReminders(): UseQueryResult<SeasonReminderDto[]> {
  return useQuery({
    queryKey: proposalKeys.seasonReminders(),
    queryFn: () => seasonReminderService.list(),
  })
}

/** Take a reminder off the list. */
export function useDismissSeasonReminder(): UseMutationResult<
  void,
  Error,
  SeasonReminderActionInput
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input) => seasonReminderService.dismiss(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: proposalKeys.seasonReminders(),
      })
    },
  })
}

/**
 * Start a rebid. Both lists change: the reminder leaves "Coming up" and a
 * new draft joins the proposals.
 */
export function useStartRebid(): UseMutationResult<
  SeasonRebidDto,
  Error,
  SeasonReminderActionInput
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input) => seasonReminderService.rebid(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: proposalKeys.seasonReminders(),
      })
      void queryClient.invalidateQueries({ queryKey: proposalKeys.lists() })
      // The client page lists that client's proposals, including the new draft.
      void queryClient.invalidateQueries({ queryKey: clientKeys.details() })
    },
  })
}
